import moment from 'moment'
import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import { appConfig } from '@/app-config/appConfig'
import { useCompanyFeatureFlags } from '@/app-config/featureFlags'
import CompanyTypeModal from '@/atoms/CompanyTypeModal/CompanyTypeModal'
import { ModalContextProvider } from '@/atoms/Modal/Modal'
import { ShowSessionTimeoutModal } from '@/atoms/SessionTimeoutModal/ShowSessionTimeoutModal'
import { TabPanelContextProvider } from '@/atoms/Tabs/TabPanel'
import { internalLogin } from '@/lib/auth'
import { hideGenesysChatbot, initGenesysCloudChat, showGenesysChatbot } from '@/lib/genesys-chat.utils'
import history from '@/lib/history'
import { getCurrentLocale } from '@/lib/i18n/locale-utils'
import loadGoogleMapsScript from '@/lib/initializeGmap'
import { WindowSizeContextProvider } from '@/lib/react-hooks/useBreakPoints'
import { Routes } from '@/lib/routes'
import { CompanyTypeModalActions, IAction } from '@/redux/actions'
import { getStore } from '@/redux/stores'
import { ApplicationState } from '@/redux/stores/types'
import { Company, CompanyTypeModal as ICompanyTypeModal } from '@/travelsuit'
import { track, trackEvent, useTracking } from '@/travelsuit/analytics'
import { canLoadUserPermissions } from '@/travelsuit/permissions'
import { Locales } from '@/types/locale'
import { User } from '@/types/user'

import AppRouter from './AppRouter'
import { AuthenticatedAppStateFetch } from './AuthenticatedAppStateFetch'

interface StateProps {
	myCompany: Company | null
	companyTypeModal: ICompanyTypeModal | null
	my: User
}

interface DispatchProps {
	showModalWindow(): void
	hideModalWindow(): void
}

type IProps = StateProps &
	DispatchProps & {
		className?: string
	}

function App(props: IProps) {
	const isGenesysChatInit = useRef(false)
	const companyFeatureFlags = useCompanyFeatureFlags()
	const chatEnabled = appConfig.FEATURE_GENESYS_CLOUD_CHAT || !!companyFeatureFlags?.enable_genesys_chat_bot
	const tracking = useTracking()

	useEffect(() => {
		if (!props.my) {
			return
		}

		if (chatEnabled) {
			if (!isGenesysChatInit.current) {
				initGenesysCloudChat(props.my)
				isGenesysChatInit.current = true
			}
			showGenesysChatbot()
		} else {
			hideGenesysChatbot()
			isGenesysChatInit.current = false
		}
	}, [props.my, chatEnabled])

	const applyGenericRedirect = () => {
		const { search: qsStr } = document.location
		if (qsStr.startsWith('?redir=')) {
			const url = qsStr.substr(qsStr.indexOf('?redir=') + 7)
			history.push(url)
		}
	}

	const setMomentLocale = (locale: Locales) => {
		moment.locale(locale)
	}
	const refreshUserLoginDetails = () => {
		if (
			canLoadUserPermissions(getStore()) ||
			![Routes.Login, Routes.Logout, Routes.PasswordChangeResult].includes(document.location.pathname as Routes)
		) {
			internalLogin({ store: getStore() })
		}
	}

	useEffect(() => {
		const locale = getCurrentLocale()
		loadGoogleMapsScript()
		setMomentLocale(locale)
		refreshUserLoginDetails()

		tracking.trackEvent({ action: 'Page View' })

		applyGenericRedirect()
	}, [tracking])

	const { companyTypeModal, myCompany, hideModalWindow } = props
	const shouldShowModalWindow = myCompany?.company_type !== 'normal' && companyTypeModal && companyTypeModal.showModal

	return (
		<WindowSizeContextProvider>
			<ModalContextProvider>
				<div id="wrapper" className={props.className}>
					<AuthenticatedAppStateFetch />
					<TabPanelContextProvider>
						<AppRouter />
					</TabPanelContextProvider>
					{myCompany && shouldShowModalWindow && (
						<CompanyTypeModal companyType={myCompany.company_type} onClose={hideModalWindow} />
					)}
				</div>
				<ShowSessionTimeoutModal />
			</ModalContextProvider>
		</WindowSizeContextProvider>
	)
}

@track<string>({}, { dispatch: trackEvent })
class AppTracker extends React.Component<IProps> {
	render() {
		return <App {...this.props} />
	}
}

const mapStateToProps = (state: ApplicationState): StateProps => {
	const { companyTypeModal, myCompany, auth } = state
	const internalUser = auth.get('internalUser')

	return {
		companyTypeModal,
		myCompany,
		my: internalUser,
	}
}
const mapDispatchToProps = (dispatch: Dispatch<IAction>): DispatchProps => {
	return {
		showModalWindow: () => {
			dispatch(CompanyTypeModalActions.showMyCompanyModal())
		},
		hideModalWindow: () => {
			dispatch(CompanyTypeModalActions.hideMyCompanyModal())
		},
	}
}

export default connect<StateProps, DispatchProps, {}, ApplicationState>(mapStateToProps, mapDispatchToProps)(AppTracker)
